import React from 'react';
import { Badge } from '../ui/badge';
import { 
  Shield, CheckCircle, AlertTriangle, Star, 
  FileCheck, UserCheck, Award
} from 'lucide-react';

// Badge configuration
const BADGE_CONFIG = {
  identity_verified_child: {
    label: 'Identity Verified',
    icon: UserCheck,
    color: 'bg-blue-50 text-blue-700 border-blue-200',
    tooltip: "Provider's identity has been verified by the platform",
  },
  background_check_declared: {
    label: 'Background Check',
    icon: FileCheck,
    color: 'bg-green-50 text-green-700 border-green-200',
    tooltip: 'Provider has declared they have a background check',
  },
  child_safe_experience: {
    label: 'Child-Safe Experience',
    icon: Star,
    color: 'bg-amber-50 text-amber-700 border-amber-200',
    tooltip: 'Completed multiple child-related jobs with positive parent reviews',
  },
  safety_aware_provider: {
    label: 'Safety-Aware',
    icon: Shield,
    color: 'bg-purple-50 text-purple-700 border-purple-200',
    tooltip: 'Completed platform safety checklist and acknowledged info-only rules',
  },
  installation_assistance_only: {
    label: 'Assistance Only',
    icon: AlertTriangle,
    color: 'bg-amber-50 text-amber-800 border-amber-300',
    tooltip: 'Non-certifying setup guidance only, not an official safety check',
  },
};

// Single Badge Component
export function BabyKidsTrustBadge({ badgeKey, size = 'md', showTooltip = true }) {
  const config = BADGE_CONFIG[badgeKey];
  
  if (!config) return null;
  
  const IconComponent = config.icon;
  const sizeClasses = {
    sm: 'text-xs px-1.5 py-0.5',
    md: 'text-xs px-2 py-1',
    lg: 'text-sm px-3 py-1.5',
  };
  
  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-3.5 h-3.5',
    lg: 'w-4 h-4',
  };
  
  return (
    <Badge 
      variant="outline" 
      className={`${config.color} ${sizeClasses[size]} inline-flex items-center gap-1`}
      title={showTooltip ? config.tooltip : undefined}
    >
      <IconComponent className={iconSizes[size]} />
      {size !== 'sm' && <span>{config.label}</span>}
    </Badge>
  );
}

// Badge Stack Component
export function BabyKidsBadgeStack({ badges, maxDisplay = 3, size = 'md' }) {
  if (!badges || badges.length === 0) return null;
  
  const displayBadges = badges.slice(0, maxDisplay);
  const remaining = badges.length - maxDisplay;
  
  return (
    <div className="flex flex-wrap gap-1.5 items-center">
      {displayBadges.map(badgeKey => (
        <BabyKidsTrustBadge 
          key={badgeKey} 
          badgeKey={badgeKey} 
          size={size} 
        />
      ))}
      {remaining > 0 && (
        <Badge variant="outline" className={`text-xs text-gray-500 ${size === 'sm' ? 'px-1.5 py-0.5' : 'px-2 py-1'}`}>
          +{remaining} more
        </Badge>
      )}
    </div>
  );
}

// Safety Disclaimer Component
export function SafetyDisclaimer({ text, type = 'warning' }) {
  const styles = {
    warning: {
      bg: 'bg-amber-50 border-amber-200',
      text: 'text-amber-800',
      icon: AlertTriangle,
    },
    info: {
      bg: 'bg-blue-50 border-blue-200',
      text: 'text-blue-800',
      icon: Shield,
    },
    success: {
      bg: 'bg-green-50 border-green-200',
      text: 'text-green-800',
      icon: CheckCircle,
    },
  };
  
  const style = styles[type] || styles.warning;
  const IconComponent = style.icon;
  
  return (
    <div className={`flex items-start gap-2 p-3 rounded-lg border ${style.bg}`}>
      <IconComponent className={`w-4 h-4 flex-shrink-0 mt-0.5 ${style.text}`} />
      <p className={`text-sm ${style.text}`}>{text}</p>
    </div>
  );
}

// Child Service Card Header Badge
export function ChildServiceHeader({ type }) {
  const configs = {
    standard: {
      label: 'Featured Service',
      sublabel: 'Baby & Kids',
      color: 'bg-blue-100 text-blue-800',
      icon: Award,
    },
    safety_aware: {
      label: 'Safety-Aware',
      sublabel: 'Info-only',
      color: 'bg-amber-100 text-amber-800',
      icon: Shield,
    },
    hire: {
      label: 'Hire Service',
      sublabel: 'Rental',
      color: 'bg-purple-100 text-purple-800',
      icon: Award,
    },
    subscription: {
      label: 'Subscription',
      sublabel: 'Monthly',
      color: 'bg-pink-100 text-pink-800',
      icon: Star,
    },
    party: {
      label: 'Party Service',
      sublabel: 'Entertainment',
      color: 'bg-pink-100 text-pink-800',
      icon: Award,
    },
    venue: {
      label: 'Party Venue',
      sublabel: 'Booking',
      color: 'bg-indigo-100 text-indigo-800',
      icon: Award,
    },
  };
  
  const config = configs[type] || configs.standard;
  const IconComponent = config.icon;
  
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg ${config.color}`}>
      <IconComponent className="w-4 h-4" />
      <div className="text-sm">
        <span className="font-medium">{config.label}</span>
        <span className="mx-1">·</span>
        <span className="opacity-75">{config.sublabel}</span>
      </div>
    </div>
  );
}

// Global disclaimer component for entertainment services
export function EntertainmentDisclaimer() {
  return (
    <SafetyDisclaimer 
      text="This is an entertainment or venue booking service. It does not provide childcare or supervision."
      type="info"
    />
  );
}

// Photo consent disclaimer
export function PhotoConsentDisclaimer() {
  return (
    <SafetyDisclaimer 
      text="Photography services require parent/guardian consent."
      type="info"
    />
  );
}

// Hire cleaning disclaimer
export function HireCleaningDisclaimer() {
  return (
    <SafetyDisclaimer 
      text="Items are cleaned between hires. This service does not provide childcare or developmental guarantees."
      type="success"
    />
  );
}

export default BabyKidsTrustBadge;
